import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIsApp } from '@/core/hooks/useIsApp';
import { postStoreClickToAppBridge, serializeStoreClickBridgePayload } from '../utils/storeAppBridge';
import type { CityFilterableService } from '../types/physicalStoreServiceContracts';
import { resolveStoreLocatorCityKey } from '../utils/resolveStoreLocatorCityKey';
import type { StoreCityGroupVm, PhysicalStoreVm } from '../viewModels/physicalStore.vm';

type UseStoreLocatorShellArgs = {
  service: CityFilterableService;
  cityGroups: StoreCityGroupVm[];
  /** Preferred city to select on load (defaults to New Delhi when omitted). */
  initialCityName?: string;
};

export function useStoreLocatorShell({ service, cityGroups, initialCityName }: UseStoreLocatorShellArgs) {
  const navigate = useNavigate();
  const isApp = useIsApp();
  const cities = useMemo(() => cityGroups.map((g) => g.cityName), [cityGroups]);

  const defaultCityKey = useMemo(
    () => resolveStoreLocatorCityKey(cityGroups, initialCityName),
    [cityGroups, initialCityName]
  );

  const [userSelectedCity, setUserSelectedCity] = useState<string | null>(null);
  const [userMobileFilter, setUserMobileFilter] = useState<string | 'all' | null>(null);

  const effectiveSelectedCity = useMemo(() => {
    if (userSelectedCity && cities.includes(userSelectedCity)) return userSelectedCity;
    return defaultCityKey;
  }, [cities, userSelectedCity, defaultCityKey]);

  const effectiveMobileFilter = useMemo(() => {
    if (userMobileFilter !== null) return userMobileFilter;
    return 'all';
  }, [userMobileFilter]);

  const filteredMobileGroups = useMemo(
    () => service.filterCityGroups(cityGroups, effectiveMobileFilter),
    [service, cityGroups, effectiveMobileFilter]
  );

  const selectedCityStores = useMemo(() => {
    if (!effectiveSelectedCity) return [];
    const group = cityGroups.find((g) => g.cityName === effectiveSelectedCity);
    return group?.stores ?? [];
  }, [cityGroups, effectiveSelectedCity]);

  const handleStoreSelect = (store: PhysicalStoreVm) => {
    // `storeUrl` is the backend-provided, already-fully-encoded catalog path segment
    // (see `OtherStoresWidget`, which uses the same field). Do NOT rebuild it via
    // `buildStoreCatalogUrl`/`store.sid`: `sid` here is already double-base64-encoded
    // by this API, so re-encoding it through `buildStoreCatalogUrl` triple-encodes it
    // and produces a broken store PLP URL.
    const catalogUrl = `/stores/${store.storeUrl}/catalog.html`;
    const { bridgeData, iosPayload, androidPayload } = serializeStoreClickBridgePayload(store);
    console.log('[StoreLocator] mobile store card clicked', {
      storeName: store.storeName,
      storeUrl: store.storeUrl,
      catalogUrl,
      isApp,
      openVia: isApp ? 'native-bridge' : 'react-router-navigate',
      bridgeData,
      iosPayload: isApp ? iosPayload : undefined,
      androidPayload: isApp ? androidPayload : undefined,
      hasAndroidBridge: typeof window !== 'undefined' && Boolean(window.AndroidBridge?.setData),
      hasIosBridge: typeof window !== 'undefined' && Boolean(window.setDataForIOS),
    });
    if (isApp) {
      postStoreClickToAppBridge(store, catalogUrl);
      return;
    }
    navigate(catalogUrl);
  };

  const handleSelectCity = (city: string) => {
    setUserSelectedCity(city);
  };

  const handleMobileFilterChange = (city: string | 'all') => {
    setUserMobileFilter(city);
  };

  return {
    cities,
    effectiveSelectedCity,
    effectiveMobileFilter,
    filteredMobileGroups,
    selectedCityStores,
    handleStoreSelect,
    handleSelectCity,
    handleMobileFilterChange,
  };
}
